// Canonical phone format. The single source is [공통] 00_입력검증표준 §2.1, which
// PRD 21 §3.3.1 was migrated into (v2.7, 2026-07-10):
//
//   입력·검증·저장 모두 하이픈 없는 11자리 숫자, `010` 시작 고정 (^010\d{8}$).
//   10자리·01X(011/016/017/018/019) 구형 번호 불허.
//   화면 표시 시에만 010-XXXX-XXXX 하이픈 삽입.
//
// Note the first word: 입력. Hyphens are not an accepted input that gets cleaned
// up on save — they are not accepted at all. That is what SHCT-523's sibling
// ticket SHCT-695 turns on, and why `toPhoneInputValue` below belongs to every
// phone field rather than to one screen that happened to get it right.

// Canonical (공통 00 §2.1): exactly 11 digits, '010'-prefixed. 10-digit / 01X
// legacy numbers are not allowed. Test against the digits-only (normalized) value.
export const PHONE_REGEX = /^010\d{8}$/;

// Canonical length, and therefore the input's own cap.
export const PHONE_DIGITS_MAX = 11;

// SHCT-695 — what a phone <input> is allowed to contain while being typed.
// Drops every non-digit (so a pasted `010-1234-5678` or `+82 10 …` collapses to
// digits instead of being rejected at submit) and stops at 11 so the field
// cannot hold a value the canonical regex would refuse.
//
// Pair it with `inputMode="numeric"` — and with NOTHING else.
//
// In particular do NOT put `maxLength` on the element. The browser applies
// maxLength to the RAW string, before React's onChange runs, so a formatted
// paste loses digits to the separators: `010-1234-5678` is 13 characters, gets
// truncated to the 11 characters `010-1234-56`, and only then reaches this
// function, which strips the two hyphens and yields the 9-digit `010123456`.
// The user sees a silently mangled number with no error. Raising the cap does
// not fix it either — `+82 10-1234-5678` would still lose digits at any finite
// character limit. This function is the whole guarantee: it already clamps to
// PHONE_DIGITS_MAX, so a 12th typed digit cannot stick.
export function toPhoneInputValue(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\D/g, '').slice(0, PHONE_DIGITS_MAX);
}

// Strips non-digit characters. Returns `null` when the result is empty so
// callers can persist `null` for "phone omitted" (PRD 03 §3.3 / PRD 15 §3.2).
// Does NOT validate — feed the result to PHONE_REGEX.test() if needed.
export function normalizePhone(value: string | null | undefined): string | null {
  if (value == null) return null;
  const digits = value.replace(/\D/g, '');
  return digits.length === 0 ? null : digits;
}

// Display formatter for the canonical 11-digit phone. Returns `'-'` for null
// (PRD 03 §3.1). Non-canonical values render verbatim so legacy rows still
// display while data migration is in flight.
export function formatPhoneForDisplay(value: string | null | undefined): string {
  if (value == null || value === '') return '-';
  if (PHONE_REGEX.test(value)) {
    return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`;
  }
  return value;
}
